import React, { useEffect } from 'react';
import { CheckCircle, X, AlertCircle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastProps {
	message: string;
	type: ToastType;
	onClose: () => void;
	duration?: number;
}

export const Toast: React.FC<ToastProps> = ({ message, type, onClose, duration = 3000 }) => {
	useEffect(() => {
		const timer = setTimeout(() => {
			onClose();
		}, duration);

		return () => clearTimeout(timer);
	}, [duration, onClose]);

	const getIcon = () => {
		switch (type) {
			case 'success':
				return <CheckCircle className="h-5 w-5 text-green-500" />;
			case 'error':
				return <AlertCircle className="h-5 w-5 text-red-500" />;
			case 'warning':
				return <AlertCircle className="h-5 w-5 text-orange-500" />;
			case 'info':
				return <Info className="h-5 w-5 text-blue-500" />;
		}
	};

	const getBgColor = () => {
		switch (type) {
			case 'success':
				return 'bg-green-50 border-green-200';
			case 'error':
				return 'bg-red-50 border-red-200';
			case 'warning':
				return 'bg-orange-50 border-orange-200';
			case 'info':
				return 'bg-blue-50 border-blue-200';
		}
	};

	const getTextColor = () => {
		switch (type) {
			case 'success':
				return 'text-green-800';
			case 'error':
				return 'text-red-800';
			case 'warning':
				return 'text-orange-800';
			case 'info':
				return 'text-blue-800';
		}
	};

	return (
		<div className={`flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg ${getBgColor()} animate-slide-in`}>
			{getIcon()}
			<p className={`flex-1 text-sm font-medium ${getTextColor()}`}>{message}</p>
			<button onClick={onClose} className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors">
				<X className="h-4 w-4" />
			</button>
		</div>
	);
};
